/**
 * Organisation Users State Service
 *
 * Signal-based element-scoped state for the Users view.
 * Manages the combined list of human users and M2M service clients
 * belonging to the current organisation.
 *
 * @packageDocumentation
 */

import { Injectable, signal, computed } from '@angular/core';
import { IdentityType } from '../models/entity.model';

export interface OrgUserRow {
  id: string;
  displayName: string;
  email: string;
  type: IdentityType;
}

@Injectable()
export class OrgUserStateService {
  private readonly _users = signal<OrgUserRow[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _removing = signal<boolean>(false);
  private readonly _removeError = signal<string | null>(null);
  private readonly _removeSuccess = signal<string | null>(null);

  readonly users = this._users.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly removing = this._removing.asReadonly();
  readonly removeError = this._removeError.asReadonly();
  readonly removeSuccess = this._removeSuccess.asReadonly();

  readonly userCount = computed(() => this._users().length);
  readonly hasUsers = computed(() => this._users().length > 0);

  setUsers(users: OrgUserRow[]): void {
    this._users.set(users);
  }

  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  setError(error: string | null): void {
    this._error.set(error);
  }

  setRemoving(removing: boolean): void {
    this._removing.set(removing);
  }

  setRemoveError(error: string | null): void {
    this._removeError.set(error);
  }

  setRemoveSuccess(message: string | null): void {
    this._removeSuccess.set(message);
  }

  removeUsersByIds(ids: Set<string>): void {
    this._users.update(users => users.filter(u => !ids.has(u.id)));
  }

  reset(): void {
    this._users.set([]);
    this._loading.set(false);
    this._error.set(null);
    this._removing.set(false);
    this._removeError.set(null);
    this._removeSuccess.set(null);
  }
}
